import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  Badge,
  Banner,
  BodyText,
  Button,
  Collapsible,
  ConfirmationModal,
  FieldText,
  FieldTextarea,
  LoadingAnimation,
  Modal,
  Stack,
  useToast,
} from "@knowtex-ai/components";
import { EditPencil, Plus, Star, Trash, ClockRotateRight } from "iconoir-react";
import type { McPlaybookDetail } from "../../lib/mc";
import {
  useCreateMcPlaybook,
  useDeleteMcPlaybook,
  useMcPlaybookRevisionsQuery,
  useMcPlaybooksQuery,
  useUpdateMcPlaybook,
} from "../../hooks/useMcRuns";
import PlaybookChecksField, {
  ImportChecksModal,
  MAX_CHECKS,
  MAX_CHECK_CHARS,
  MAX_NOTES_CHARS,
  draftCheck,
  type DraftCheck,
} from "./PlaybookChecksField";
import PlaybookChecksList from "./PlaybookChecksList";
import type { ParsedPlaybook } from "./playbookText";
import McHeader from "./McHeader";
import McPageShell from "./McPageShell";
import { countOf, formatCount, formatDateTime, playbookAnchor } from "./format";
import { GAP } from "./layout";
import { useEditorModal } from "./useEditorModal";
import { useRetained } from "./useRetained";

interface McPlaybooksPageProps {
  setAuth: (isAuthenticated: boolean) => void;
  setUserOrg: (org: string | null) => void;
}

const MAX_TITLE_CHARS = 100;
const MAX_DESCRIPTION_CHARS = 2000;

const WRITING_TIPS = [
  "Use the description to say what the playbook is for and how far to go, like a quick post-deploy check or a full accessibility pass. Runs read it.",
  "Keep each check short: what to test, in the order MC should do it.",
  "MC already judges results against web standards and how the app should work. Add notes only for what it can't know: the messages, statuses or timings you expect, flags, roles or test data a check needs, and when it's fine to skip it.",
  "Say what to check, not how to click through the app. MC finds its own way.",
];

/** e.g. "by ada@knowtex.ai on Oct 20, 2026, 4:31 PM EDT" */
const byLine = (who?: string, iso?: string) =>
  [who && `by ${who}`, iso && `on ${formatDateTime(iso)}`]
    .filter(Boolean)
    .join(" ");

function PlaybookEditor({
  playbook,
  open,
  onClose,
}: {
  /** Undefined creates a new playbook. */
  playbook?: McPlaybookDetail;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateMcPlaybook();
  const update = useUpdateMcPlaybook();
  const [title, setTitle] = useState(playbook?.title ?? "");
  const [description, setDescription] = useState(playbook?.description ?? "");
  const [checks, setChecks] = useState<DraftCheck[]>(() =>
    playbook?.checks.length ? playbook.checks.map(draftCheck) : [draftCheck()],
  );
  const [importing, setImporting] = useState(false);
  const [importNote, setImportNote] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const filled = checks.filter((c) => c.text.trim());
  const canSave = !!title.trim() && filled.length > 0;

  const importChecks = (parsed: ParsedPlaybook) => {
    const kept = checks.filter((c) => c.text.trim() || c.notes.trim());
    const room = Math.max(0, MAX_CHECKS - kept.length);
    const trimmed = parsed.checks.some(
      (check) =>
        check.text.length > MAX_CHECK_CHARS ||
        (check.notes?.length ?? 0) > MAX_NOTES_CHARS,
    );
    const accepted = parsed.checks.slice(0, room).map((check) =>
      draftCheck({
        text: check.text.slice(0, MAX_CHECK_CHARS),
        notes: check.notes?.slice(0, MAX_NOTES_CHARS),
      }),
    );
    setChecks([...kept, ...accepted]);
    const descriptionTooLong =
      !!parsed.description &&
      !description.trim() &&
      parsed.description.length > MAX_DESCRIPTION_CHARS;
    if (parsed.description && !description.trim())
      setDescription(parsed.description.slice(0, MAX_DESCRIPTION_CHARS));
    const dropped = parsed.checks.length - accepted.length;
    const note = [
      dropped > 0 &&
        `Added ${accepted.length} of ${parsed.checks.length} checks. A playbook can have ${MAX_CHECKS}.`,
      trimmed &&
        `Shortened text that was over ${MAX_CHECK_CHARS} characters, or notes over ${MAX_NOTES_CHARS}.`,
      descriptionTooLong &&
        `Shortened the description to ${MAX_DESCRIPTION_CHARS} characters.`,
    ].filter(Boolean);
    setImportNote(note.length ? note.join(" ") : null);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    const fields = {
      title: title.trim(),
      description: description.trim(),
      checks: filled.map((c) => ({
        ...(c.id && { id: c.id }),
        text: c.text.trim(),
        ...(c.notes.trim() && { notes: c.notes.trim() }),
      })),
    };
    const onSuccess = () => {
      toast.success(playbook ? "Playbook saved" : "Playbook added");
      onClose();
    };
    if (playbook) update.mutate({ id: playbook.id, ...fields }, { onSuccess });
    else
      create.mutate(
        {
          ...fields,
          description: fields.description || undefined,
        },
        { onSuccess },
      );
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={playbook ? "Edit playbook" : "New playbook"}
      size="large"
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <FieldText
          label="Title"
          placeholder="e.g. Smoke test"
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE_CHARS))}
          maxLength={MAX_TITLE_CHARS}
          autoComplete="off"
          required
          autoFocus={!playbook}
          disabled={pending}
        />
        <FieldTextarea
          label="Description"
          placeholder="e.g. A quick check after every deploy with one patient and one short visit"
          description="Optional"
          value={description}
          onChange={(e) =>
            setDescription(e.target.value.slice(0, MAX_DESCRIPTION_CHARS))
          }
          maxLength={MAX_DESCRIPTION_CHARS}
          autoComplete="off"
          rows={3}
          resize="vertical"
          disabled={pending}
        />
        <Collapsible trigger="How to write a playbook" size="medium">
          <ul>
            {WRITING_TIPS.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </Collapsible>
        <PlaybookChecksField
          checks={checks}
          onChange={setChecks}
          onImport={() => setImporting(true)}
          disabled={pending}
        />
        {importNote && <Banner variant="warning">{importNote}</Banner>}
        {error && (
          <Banner variant="error" heading="Couldn't save the playbook">
            {error.message}
          </Banner>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button type="submit" loading={pending} disabled={!canSave}>
            {playbook ? "Save" : "Add playbook"}
          </Button>
        </div>
      </form>
      <ImportChecksModal
        open={importing}
        onClose={() => setImporting(false)}
        onImport={importChecks}
      />
    </Modal>
  );
}

function RevisionsModal({
  playbook,
  version,
  onClose,
}: {
  playbook: McPlaybookDetail | null;
  /** Opens this version instead of the newest */
  version?: number;
  onClose: () => void;
}) {
  const shown = useRetained(playbook);
  const revisionsQuery = useMcPlaybookRevisionsQuery(shown?.id ?? "", {
    enabled: !!playbook,
  });
  const revisions = revisionsQuery.data ?? [];

  return (
    <Modal
      open={!!playbook}
      onOpenChange={(next) => !next && onClose()}
      title={shown ? `${shown.title} history` : "History"}
      subtitle="Every saved version, newest first"
      size="large"
    >
      {revisionsQuery.isLoading ? (
        <div className="flex justify-center py-12 text-gray-400">
          <LoadingAnimation size={24} />
        </div>
      ) : revisionsQuery.isError ? (
        <Banner variant="error" heading="Couldn't load the history">
          {revisionsQuery.error.message}
        </Banner>
      ) : (
        <Accordion
          defaultOpen={String(
            revisions.find((r) => r.revision === version)?.revision ??
              revisions[0]?.revision ??
              "",
          )}
        >
          {revisions.map((r) => (
            <AccordionItem
              key={r.revision}
              id={String(r.revision)}
              trigger={
                <span className="flex flex-wrap items-baseline gap-x-3">
                  <span className="text-gray-900">Version {r.revision}</span>
                  <span className="text-[13px] font-normal text-gray-500">
                    Saved {byLine(r.createdBy, r.createdAt)}
                  </span>
                </span>
              }
            >
              <Stack gap={GAP.block}>
                {r.title !== shown?.title && (
                  <BodyText size="small" muted>
                    Titled {r.title}
                  </BodyText>
                )}
                {r.description && (
                  <p className="m-0 whitespace-pre-wrap break-words text-sm text-gray-800">
                    {r.description}
                  </p>
                )}
                <PlaybookChecksList
                  checks={r.checks}
                  aria-label={`Version ${r.revision} checks`}
                />
              </Stack>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </Modal>
  );
}

function PlaybookSection({
  playbook,
  onEdit,
  onHistory,
  onDelete,
}: {
  playbook: McPlaybookDetail;
  onEdit: () => void;
  onHistory: () => void;
  onDelete: () => void;
}) {
  const toast = useToast();
  const update = useUpdateMcPlaybook();
  const makeDefault = () =>
    update.mutate(
      { id: playbook.id, default: true },
      {
        onSuccess: () => toast.success(`${playbook.title} is now the default`),
        onError: (error) =>
          toast.error("Couldn't change the default", error.message),
      },
    );

  return (
    <AccordionItem
      id={playbook.id}
      trigger={
        <span
          id={playbookAnchor(playbook.id)}
          className="flex scroll-mt-6 flex-wrap items-baseline gap-x-3 gap-y-1"
        >
          <span className="text-gray-900">{playbook.title}</span>
          {playbook.default && (
            <Badge size="small" variant="info">
              Default
            </Badge>
          )}
          <span className="text-[13px] font-normal text-gray-500">
            {countOf(playbook.checks.length, "check")}
          </span>
        </span>
      }
    >
      <Stack gap={GAP.block}>
        {playbook.description && (
          <p className="m-0 whitespace-pre-wrap break-words text-sm text-gray-800">
            {playbook.description}
          </p>
        )}
        <PlaybookChecksList
          checks={playbook.checks}
          aria-label={`${playbook.title} checks`}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="small"
              icon={EditPencil}
              onClick={onEdit}
            >
              Edit
            </Button>
            {!playbook.default && (
              <Button
                variant="secondary"
                size="small"
                icon={Star}
                onClick={makeDefault}
                loading={update.isPending}
              >
                Make default
              </Button>
            )}
            <Button
              variant="secondary"
              size="small"
              icon={ClockRotateRight}
              onClick={onHistory}
            >
              History ({formatCount(playbook.revision)})
            </Button>
            <Button
              variant="secondary-danger"
              size="small"
              icon={Trash}
              onClick={onDelete}
            >
              Delete
            </Button>
          </div>
          <span className="text-[13px] text-gray-500">
            Updated {byLine(playbook.updatedBy, playbook.updatedAt)}
          </span>
        </div>
      </Stack>
    </AccordionItem>
  );
}

function McPlaybooks() {
  const toast = useToast();
  const playbooksQuery = useMcPlaybooksQuery();
  const remove = useDeleteMcPlaybook();
  const playbooks = useMemo(
    () => playbooksQuery.data ?? [],
    [playbooksQuery.data],
  );
  const editor = useEditorModal<McPlaybookDetail>();
  const [history, setHistory] = useState<McPlaybookDetail | null>(null);
  const [deleting, setDeleting] = useState<McPlaybookDetail | null>(null);
  const confirmDelete = useRetained(deleting);
  const linked = useLocation().hash.slice(1);
  const [params, setParams] = useSearchParams();
  const linkedHistory = params.get("history");
  const linkedVersion = Number(params.get("version")) || undefined;
  const loaded = playbooksQuery.isSuccess;
  useEffect(() => {
    if (loaded && linked)
      document.getElementById(linked)?.scrollIntoView({ block: "start" });
  }, [loaded, linked]);
  useEffect(() => {
    const p = linkedHistory && playbooks.find((x) => x.id === linkedHistory);
    if (p) setHistory(p);
  }, [linkedHistory, playbooks]);
  const closeHistory = () => {
    setHistory(null);
    if (linkedHistory) setParams({}, { replace: true });
  };

  const deletePlaybook = () => {
    if (!deleting) return;
    const { id, title } = deleting;
    remove.mutate(id, {
      onSuccess: () => toast.success(`Deleted ${title}`),
      onError: (error) =>
        toast.error("Couldn't delete the playbook", error.message),
    });
    setDeleting(null);
  };

  const body = () => {
    if (playbooksQuery.isLoading) {
      return (
        <div className="flex justify-center py-24 text-gray-400">
          <LoadingAnimation size={28} />
        </div>
      );
    }
    if (playbooksQuery.isError) {
      return (
        <Banner variant="error" heading="Couldn't load the playbooks">
          {playbooksQuery.error.message}
        </Banner>
      );
    }
    if (!playbooks.length) {
      return (
        <Banner variant="info" heading="No playbooks yet">
          Add one to tell MC what to check on every run. Until then, runs need
          custom instructions.
        </Banner>
      );
    }
    const opened = playbooks.find((p) => linked === playbookAnchor(p.id));
    return (
      <Accordion size="large" defaultOpen={opened?.id}>
        {playbooks.map((playbook) => (
          <PlaybookSection
            key={playbook.id}
            playbook={playbook}
            onEdit={() => editor.edit(playbook)}
            onHistory={() => setHistory(playbook)}
            onDelete={() => setDeleting(playbook)}
          />
        ))}
      </Accordion>
    );
  };

  return (
    <>
      <McHeader
        section="playbooks"
        actions={
          <Button icon={Plus} onClick={editor.add}>
            New playbook
          </Button>
        }
      />
      <div className="mt-8">
        <Stack gap={GAP.page}>
          <Banner variant="brand" heading="How playbooks work">
            A playbook is the list of checks MC works through on a run, like a
            quick pass after each deploy or a full accessibility review. Runs
            use the default playbook unless you pick another when you start one.
            Edits apply to the next run, and every earlier version stays in
            History.
          </Banner>
          {body()}
        </Stack>
      </div>
      {editor.state && (
        <PlaybookEditor
          key={editor.state.key}
          playbook={editor.state.item ?? undefined}
          open={editor.state.open}
          onClose={editor.close}
        />
      )}
      <RevisionsModal
        playbook={history}
        version={history?.id === linkedHistory ? linkedVersion : undefined}
        onClose={closeHistory}
      />
      <ConfirmationModal
        open={!!deleting}
        title={`Delete ${confirmDelete?.title ?? "this playbook"}?`}
        continueText="Delete playbook"
        cancelText="Close"
        continueVariant="primary-danger"
        onContinue={deletePlaybook}
        onCancel={() => setDeleting(null)}
      >
        {confirmDelete?.default
          ? "This is the default playbook; the oldest remaining one becomes the default. Past runs keep the checks they ran with."
          : "Past runs keep the checks they ran with."}
      </ConfirmationModal>
    </>
  );
}

export default function McPlaybooksPage({
  setAuth,
  setUserOrg,
}: McPlaybooksPageProps) {
  return (
    <McPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <McPlaybooks />
    </McPageShell>
  );
}
